import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Clock, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";

const FIELD =
  "w-full bg-white border border-gold-subtle rounded-md text-navy font-body text-sm md:text-base py-3 px-4 outline-none focus:border-barn focus:ring-2 focus:ring-barn/20 transition-[border-color,box-shadow] duration-200 placeholder:text-ink/40";

export default function Contact({ contact, services }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email, and a message.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/leads", form);
      toast.success(data.message || "Thank you — we'll be in touch shortly.");
      setForm({ name: "", email: "", company: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const details = [
    { icon: Mail, label: "Email", value: contact?.email },
    { icon: Phone, label: "Phone", value: contact?.phone },
    { icon: MapPin, label: "Based In", value: contact?.address },
    { icon: Clock, label: "Hours", value: contact?.hours },
  ];

  return (
    <section id="contact" className="relative bg-white py-16 md:py-24 lg:py-28" data-testid="contact">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16">
        <div>
          <span className="overline text-barn">{contact?.overline}</span>
          <h2 className="mt-4 font-display font-extrabold text-3xl md:text-4xl lg:text-5xl text-navy tracking-tight leading-[1.08]">
            {contact?.heading}
          </h2>
          <p className="mt-4 font-body text-base md:text-lg text-ink leading-relaxed max-w-md">{contact?.subtitle}</p>

          {contact?.backgroundImage && (
            <div className="mt-8 overflow-hidden rounded-lg aspect-[16/9] hidden lg:block shadow-[0_20px_40px_-25px_rgba(20,39,58,0.35)]">
              <img src={contact.backgroundImage} alt="Fort Worth, Texas skyline" className="w-full h-full object-cover" />
            </div>
          )}

          <div className="mt-8 grid grid-cols-2 gap-6">
            {details.map((d) => (
              <div key={d.label}>
                <div className="flex items-center gap-2 text-barn mb-1.5"><d.icon size={15} /><span className="overline text-navy-soft">{d.label}</span></div>
                <div className="font-body text-navy font-medium text-sm md:text-base break-words">{d.value}</div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="bg-cream border border-gold-subtle rounded-xl p-6 md:p-9"
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="overline text-navy-soft block mb-2">Name *</label>
                <input data-testid="contact-name" className={FIELD} value={form.name} onChange={set("name")} placeholder="Jane Doe" />
              </div>
              <div>
                <label className="overline text-navy-soft block mb-2">Email *</label>
                <input data-testid="contact-email" type="email" className={FIELD} value={form.email} onChange={set("email")} placeholder="jane@company.com" />
              </div>
              <div>
                <label className="overline text-navy-soft block mb-2">Company</label>
                <input data-testid="contact-company" className={FIELD} value={form.company} onChange={set("company")} placeholder="Acme Aerospace" />
              </div>
              <div>
                <label className="overline text-navy-soft block mb-2">Phone</label>
                <input data-testid="contact-phone" className={FIELD} value={form.phone} onChange={set("phone")} placeholder="+1 (817) …" />
              </div>
            </div>

            <div className="mt-5">
              <label className="overline text-navy-soft block mb-2">Interested In</label>
              <select data-testid="contact-service" className={`${FIELD} cursor-pointer`} value={form.service} onChange={set("service")}>
                <option value="">Select a capability…</option>
                {services?.map((s) => <option key={s.id} value={s.title}>{s.title}</option>)}
                <option value="General inquiry">General inquiry</option>
              </select>
            </div>

            <div className="mt-5">
              <label className="overline text-navy-soft block mb-2">What's hard to source? *</label>
              <textarea data-testid="contact-message" rows={4} className={`${FIELD} resize-none`} value={form.message} onChange={set("message")} placeholder="Tell us about the part, commodity, or challenge…" />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-btn"
              className="mt-7 w-full font-body font-bold text-sm px-8 py-4 bg-barn text-white rounded-md hover:bg-[#9c3227] transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <>Send Inquiry <ArrowRight size={16} /></>}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
